import { inject, Injectable } from "@angular/core";
import { Action, select, Selector, State, StateContext } from "@ngxs/store";
import { SchoolService } from "@platform-app/app/core/api/services";
import { AuthState } from "@platform-app/app/core/auth/auth.state";
import { StateToDefaultResetter } from "@platform-app/app/shared/common/store/state-to-default-resetter/state-to-default-resetter";
import {
  CheckSchoolProgramPreferencesSettings,
  GetSchoolWorkspaceSettings,
  UpdateSchoolWorkspaceSettings,
} from "@platform-app/app/shared/schools/school-workspace-settings.actions";
import { tap } from "rxjs";

export interface SchoolWorkspaceSettingsStateModel {
  enableStudentSchedulePreferences: boolean;
}

const DEFAULT_STATE: SchoolWorkspaceSettingsStateModel = {
  enableStudentSchedulePreferences: false,
};

@State<SchoolWorkspaceSettingsStateModel>({
  name: "SchoolWorkspaceSettings",
  defaults: DEFAULT_STATE,
})
@Injectable()
export class SchoolWorkspaceSettingsState extends StateToDefaultResetter<SchoolWorkspaceSettingsStateModel> {
  private readonly schoolService = inject(SchoolService);
  private readonly organizationId = select(AuthState.organizationId);

  constructor() {
    super(DEFAULT_STATE);
  }

  @Selector()
  static settings(state: SchoolWorkspaceSettingsStateModel) {
    return state;
  }

  @Selector()
  static enableSchedulePreferences(state: SchoolWorkspaceSettingsStateModel) {
    return state.enableStudentSchedulePreferences;
  }

  @Action(GetSchoolWorkspaceSettings)
  @Action(CheckSchoolProgramPreferencesSettings)
  getSchoolWorkspaceSettings(
    ctx: StateContext<SchoolWorkspaceSettingsStateModel>,
  ) {
    return this.schoolService
      .getSchoolWorkspaceSettings({ schoolId: this.organizationId()! })
      .pipe(tap((response) => ctx.patchState(response)));
  }

  @Action(UpdateSchoolWorkspaceSettings)
  updateSchoolWorkspaceSettings(
    ctx: StateContext<SchoolWorkspaceSettingsStateModel>,
    action: UpdateSchoolWorkspaceSettings,
  ) {
    return this.schoolService
      .updateSchoolWorkspaceSettings({
        schoolId: this.organizationId()!,
        body: {
          enableStudentSchedulePreferences:
            action.payload.enableStudentSchedulePreferences,
        },
      })
      .pipe(
        tap(() =>
          ctx.patchState({
            enableStudentSchedulePreferences:
              action.payload.enableStudentSchedulePreferences,
          }),
        ),
      );
  }
}
